/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';
import { toast } from 'react-toastify';

import { request } from '@mairistem/tools';
import {
  getDownloadPecoto,
  uploadGenerationPecoto,
  numerotationIndividu,
  denumerotationIndividu,
  clotureTrimestre,
} from '@/routes';
import type { PecotoGenerationResponse, Trimestre } from '@/types';
import { GestionTrimestreActions } from '@/enums';
import { declotureTrimestre, editionsTrimestre } from '@/routes/trimestre';
import { useParametresStore } from '@/zustand';

export type TrimestrePartial = Partial<Trimestre>;

const useGestionTrimestre = (initialTrimestre = null) => {
  const [trimestre, setTrimestre] =
    React.useState<TrimestrePartial>(initialTrimestre);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [dateCloture, setDateCloture] = React.useState<Date | null>(null);
  const [numerotation, setNumerotation] = React.useState<Date | null>(null);
  const [generation, setGeneration] = React.useState<Date | null>(null);
  const [edition, setEdition] = React.useState<Date | null>(null);
  const [telechargement, setTelechargement] = React.useState<boolean>(false);
  const [pecotoId, setPecotoId] = React.useState<number | null>(null);
  const [modalOpen, setModalOpen] = React.useState<boolean>(false);
  const [confirmationModalOpen, setConfirmationModalOpen] =
    React.useState<boolean>(false);
  const [editionModalOpen, setEditionModalOpen] =
    React.useState<boolean>(false);
  const { periode, annee } = trimestre || {};

  const parametres = useParametresStore((state) => state.parametres);

  const initializeTrimestre = React.useCallback((data: TrimestrePartial) => {
    if (!data) return;

    setTrimestre(data);
    setNumerotation(
      !_.isNil(data?.dateNumerotation)
        ? new Date(data?.dateNumerotation)
        : null,
    );
    setDateCloture(
      !_.isNil(data?.dateCloture) ? new Date(data?.dateCloture) : null,
    );
    setPecotoId(data?.identifiantPecoto);
    setGeneration(
      !_.isNil(data?.pecoto?.date) ? new Date(data?.pecoto?.date) : null,
    );
    setTelechargement(data?.pecoto?.telecharge);
    setEdition(
      !_.isNil(data?.dateEdition) ? new Date(data?.dateEdition) : null,
    );
  }, []);

  const handleNumerotation = React.useCallback(
    (action: GestionTrimestreActions) => {
      setLoading(true);
      const apiAction =
        action === GestionTrimestreActions.DENUMEROTATION
          ? denumerotationIndividu()
          : numerotationIndividu();

      return request
        .post(apiAction, { annee, periode })
        .then((count: number) => {
          if (
            action === GestionTrimestreActions.NUMEROTATION ||
            action === GestionTrimestreActions.RENUMEROTATION
          ) {
            setNumerotation(new Date());
            toast(
              count > 0
                ? `${count} individu${count > 1 ? 's' : ''} numéroté${count > 1 ? 's' : ''}`
                : 'Numérotation effectuée (aucun individu à numéroter)',
              { type: 'success' },
            );
          } else {
            setNumerotation(null);
            toast(
              count > 0
                ? `${count} individu${count > 1 ? 's' : ''} dénuméroté${count > 1 ? 's' : ''}`
                : 'Dénumérotation effectuée (aucun individu à dénuméroter)',
              { type: 'success' },
            );
          }
          setGeneration(null);
          setTelechargement(false);

          return true;
        })
        .catch(() => {
          toast('Une erreur est survenue lors de la numérotation', {
            type: 'error',
          });
          return false;
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [annee, periode],
  );

  const handleGeneration = React.useCallback(() => {
    if (_.isNil(parametres)) {
      toast(
        "La génération n'est pas possible. Veuillez renseigner des paramétrages",
        {
          type: 'warning',
        },
      );
      return Promise.resolve(false);
    }

    setLoading(true);

    return request
      .custom('post', uploadGenerationPecoto(annee, periode), undefined, {
        headers: { Accept: '*/*' },
        responseType: 'blob',
      })
      .then(
        async (response: { data: Blob; headers: Record<string, string> }) => {
          const contentType = response.headers['content-type'] || '';

          if (contentType.includes('application/pdf')) {
            const url = URL.createObjectURL(response.data);
            window.open(url, '_blank');

            setPecotoId(null);
            setGeneration(null);
            toast(
              'La génération a échoué, le rapport des anomalies a été ouvert dans un nouvel onglet',
              { type: 'error' },
            );
            return false;
          }

          const { data }: PecotoGenerationResponse = JSON.parse(
            await response.data.text(),
          );
          setPecotoId(data.identifiant);
          setGeneration(new Date());
          toast('Génération effectuée avec succès', { type: 'success' });
          return true;
        },
      )
      .catch(() => {
        toast('Une erreur est survenue lors de la génération', {
          type: 'error',
        });
        return false;
      })
      .finally(() => {
        setLoading(false);
      });
  }, [annee, periode, parametres]);

  const handleTelechargement = React.useCallback(() => {
    setLoading(true);

    return request
      .get(getDownloadPecoto(pecotoId), {
        headers: { Accept: '*/*' },
        responseType: 'blob',
      })
      .then((d: Blob) => {
        const url = URL.createObjectURL(d);
        const link = document.createElement('a');
        link.href = url;
        link.download = `RE_${annee}${periode}.txt`;
        link.click();
        setTelechargement(true);
      })
      .catch(() => {
        toast('Une erreur est survenue lors du téléchargement', {
          type: 'error',
        });
        return false;
      })
      .finally(() => {
        setLoading(false);
      });
  }, [pecotoId, annee, periode]);

  const handleCloture = React.useCallback(() => {
    const apiAction = _.isNil(dateCloture)
      ? clotureTrimestre(trimestre?.identifiant)
      : declotureTrimestre(trimestre?.identifiant);
    setLoading(true);

    return request
      .post(apiAction)
      .then((data: TrimestrePartial) => {
        toast(
          `Trimestre ${_.isNil(dateCloture) ? 'clôturé' : 'déclôturé'} avec succès`,
          { type: 'success' },
        );
        initializeTrimestre(data);
        setModalOpen(false);
        return true;
      })
      .catch(() => {
        toast(
          `Une erreur est survenue lors de la ${_.isNil(dateCloture) ? 'clôturé' : 'déclôturé'}`,
          {
            type: 'error',
          },
        );
        return false;
      })
      .finally(() => {
        setConfirmationModalOpen(false);
        setLoading(false);
      });
  }, [trimestre?.identifiant, initializeTrimestre, dateCloture]);

  const handleEdition = React.useCallback(
    (data) => {
      setLoading(true);
      return request
        .post(editionsTrimestre(trimestre?.identifiant), data, {
          headers: { Accept: '*/*' },
          responseType: 'blob',
        })
        .then((response: any) => {
          // Get Content-Type from response
          const contentType = response.type || '';

          // Create a URL for the blob
          const url = URL.createObjectURL(response);

          if (contentType.includes('application/pdf')) {
            const blob = response.data || response;

            // Créer un nouveau blob avec le bon type
            const pdfBlob = new Blob([blob], { type: 'application/pdf' });

            // Créer un File object avec le bon nom
            const url2 = URL.createObjectURL(pdfBlob);

            // If PDF, open in new tab
            window.open(url2, '_blank');
          } else if (contentType.includes('application/zip')) {
            // If ZIP, trigger download
            const link = document.createElement('a');
            link.href = url;
            link.download = `EditionsListe_${trimestre?.periode}_${trimestre?.annee}.zip`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          } else {
            toast('Format de fichier non reconnu', { type: 'error' });
            return false;
          }
          setEdition(new Date());
          return true;
        })
        .catch(() => {
          toast("Une erreur est survenue lors de l'édition des listes", {
            type: 'error',
          });

          return false;
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [trimestre?.annee, trimestre?.identifiant, trimestre?.periode],
  );

  const handleModalCloture = React.useCallback(() => {
    setConfirmationModalOpen((prev) => !prev);

    return Promise.resolve(true);
  }, []);

  const handleGestionTrimestreModalOpen = React.useCallback(
    () => setModalOpen(true),
    [],
  );
  const handleGestionTrimestreModalClose = React.useCallback(
    () => setModalOpen(false),
    [],
  );

  const handleEditionModalOpen = React.useCallback(
    () => setEditionModalOpen(true),
    [],
  );
  const handleEditionModalClose = React.useCallback(
    () => setEditionModalOpen(false),
    [],
  );

  return {
    states: {
      trimestre,
      loading,
      dateCloture,
      numerotation,
      generation,
      edition,
      telechargement,
      pecotoId,
      modalOpen,
      confirmationModalOpen,
      editionModalOpen,
    },
    actions: {
      setTrimestre,
      setLoading,
      initializeTrimestre,
      handleNumerotation,
      handleGeneration,
      handleTelechargement,
      handleEdition,
      handleCloture,
      handleModalCloture,
      handleGestionTrimestreModalOpen,
      handleGestionTrimestreModalClose,
      handleEditionModalOpen,
      handleEditionModalClose,
      setConfirmationModalOpen,
    },
  };
};

export default useGestionTrimestre;
